import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';

import { AdminPageHeader } from '@/components/admin/admin-page-header';
import { FilterBar } from '@/components/admin/filter-bar';
import { InvitationsTable } from '@/components/admin/invitations/invitations-table';
import { InvitationCreate } from '@/components/admin/invitations/invitation-create/invitation-create';
import { BulkDeleteBar } from '@/components/admin/shared/bulk-delete-bar';
import { OrganizationPicker } from '@/components/admin/shared/organization-picker';
import { useBulkSelection } from '@/components/admin/shared/use-bulk-selection';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  useAdminInvitations,
  useBulkDeleteInvitations,
} from '@/hooks/data/admin/use-invitations';
import { useDebounced } from '@/hooks/primitives/use-debounced';
import type { InvitationStatus } from '@/types/generated';
import { loadNamespaces } from '@/lib/i18n/i18n';

const searchSchema = z.object({
  organization_id: z.number().int().positive().optional(),
  status: z.enum(['PENDING', 'EXPIRED']).optional(),
  q: z.string().optional(),
  offset: z.number().int().nonnegative().default(0),
});

const LIMIT = 20;

export const Route = createFileRoute('/_authenticated/admin/invitations')({
  loader: () => loadNamespaces('settings'),
  validateSearch: searchSchema,
  staticData: { title: 'Invitations' },
  component: AdminInvitationsPage,
});

function AdminInvitationsPage(): React.ReactElement {
  const { organization_id, status, q, offset } = Route.useSearch();
  const navigate = Route.useNavigate();
  const [createOpen, setCreateOpen] = useState(false);
  const bulkDelete = useBulkDeleteInvitations();

  const [searchInput, setSearchInput] = useState(q ?? '');
  const debouncedSearch = useDebounced(searchInput, 300);

  const invitationsQuery = useAdminInvitations({
    organization_id,
    status,
    email: debouncedSearch || undefined,
    limit: LIMIT,
    offset,
  });

  const bulk = useBulkSelection({
    rows: invitationsQuery.data?.items ?? [],
    getId: (inv) => inv.id,
    getLabel: (inv) => inv.email,
  });

  const setOrgId = (value: number | undefined): void => {
    void navigate({
      search: (s) => ({ ...s, organization_id: value, offset: 0 }),
    });
  };

  const setStatus = (value: string): void => {
    void navigate({
      search: (s) => ({
        ...s,
        status: value === 'ALL' ? undefined : (value as InvitationStatus),
        offset: 0,
      }),
    });
  };

  const onSearchChange = (value: string): void => {
    setSearchInput(value);
    void navigate({
      search: (s) => ({ ...s, q: value || undefined, offset: 0 }),
    });
  };

  return (
    <main>
      <AdminPageHeader
        title="Invitations"
        description="View pending invitations across all organizations"
        actions={
          <Button onClick={() => setCreateOpen(true)}>Invite user</Button>
        }
      />
      <InvitationCreate open={createOpen} onOpenChange={setCreateOpen} />
      <FilterBar>
        <OrganizationPicker value={organization_id} onChange={setOrgId} />
        <Select value={status ?? 'ALL'} onValueChange={setStatus}>
          <SelectTrigger className="w-36">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All statuses</SelectItem>
            <SelectItem value="PENDING">Pending</SelectItem>
            <SelectItem value="EXPIRED">Expired</SelectItem>
          </SelectContent>
        </Select>
        <Input
          placeholder="Search by email…"
          value={searchInput}
          onChange={(e) => onSearchChange(e.target.value)}
          className="w-64"
        />
      </FilterBar>
      <BulkDeleteBar
        items={bulk.items}
        onClear={bulk.clear}
        onSelectionChange={bulk.setSelectedIds}
        resource="invitation"
        isPending={bulkDelete.isPending}
        onDelete={bulkDelete.mutateAsync}
      />
      <InvitationsTable
        query={invitationsQuery}
        offset={offset}
        limit={LIMIT}
        onPageChange={(newOffset) =>
          void navigate({ search: (s) => ({ ...s, offset: newOffset }) })
        }
        selection={bulk.selection}
      />
    </main>
  );
}
